import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Mail, Copy } from "lucide-react";
import { triggerToast } from "../../../utils/toast";
import styles from "../sidequest.module.scss";
import mainlogo from "../../../assets/images/new_web_logo.png";

const PARTNERSHIP_EMAIL = "partnerships@keyquest.io";
const SUBJECT = "Sidequest Partnership Inquiry";
const BODY = "Hi KeyQuest team,\n\nI'd like to ask about the Sidequest partnership program.\n\n";

// A bare `mailto:` link hands off to the OS, which — if more than one app can
// handle it (Outlook, plus a browser once Chrome/Edge has a web mail handler
// registered) — pops up its own "how do you want to open this?" chooser.
// Picking Outlook works; picking the browser usually does nothing, because
// the browser was never actually configured with a web mail handler, so it
// has no idea what to do with a raw mailto URI. That's an OS/browser
// configuration issue this page can't fix directly, so instead of fighting
// the chooser, we give people a direct choice up front: jump straight into
// Gmail or Outlook's own web compose (both pre-filled, and both work in any
// browser with no OS dialog at all), fall back to the native mailto: for
// whatever's set as the system default, or just copy the address.
const EmailContact = () => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const handleGmail = () => {
    const params = new URLSearchParams({
      view: "cm",
      fs: "1",
      to: PARTNERSHIP_EMAIL,
      su: SUBJECT,
      body: BODY,
    });
    openInNewTab(`https://mail.google.com/mail/?${params.toString()}`);
  };

  const handleOutlookWeb = () => {
    const params = new URLSearchParams({
      to: PARTNERSHIP_EMAIL,
      subject: SUBJECT,
      body: BODY,
    });
    openInNewTab(`https://outlook.office.com/mail/deeplink/compose?${params.toString()}`);
  };

  const handleDefaultApp = () => {
    const params = new URLSearchParams({ subject: SUBJECT, body: BODY });
    window.location.href = `mailto:${PARTNERSHIP_EMAIL}?${params.toString()}`;
    setOpen(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(PARTNERSHIP_EMAIL);
      triggerToast("Email address copied to clipboard", "success");
    } catch (e) {
      triggerToast("Couldn't copy the address — please copy it manually.", "error");
    }
    setOpen(false);
  };

  return (
    <span className={styles.emailContact} ref={wrapRef}>
      <button
        type="button"
        className={styles.emailTrigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {PARTNERSHIP_EMAIL}
        <ChevronDown size={14} />
      </button>

      {open && (
        <div className={styles.emailMenu} role="menu">
          <button type="button" role="menuitem" onClick={handleGmail}>
            <Mail size={14} /> Open in Gmail
          </button>
          <button type="button" role="menuitem" onClick={handleOutlookWeb}>
            <Mail size={14} /> Open in Outlook Web
          </button>
          <button type="button" role="menuitem" onClick={handleDefaultApp}>
            <Mail size={14} /> Open in default mail app
          </button>
          <button type="button" role="menuitem" onClick={handleCopy}>
            <Copy size={14} /> Copy email address
          </button>
        </div>
      )}
    </span>
  );
};

const Footer = () => {
  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <img src={mainlogo} alt="KeyQuest" />
        <p>
          Questions about the partnership? <EmailContact />
        </p>
        <div className={styles.footerLinks}>
          <Link to="/">Home</Link>
          &middot;
          <Link to="/privacy">Privacy</Link>
          &middot;
          <Link to="/terms">Terms</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
